/**
 * Splitting the recovery key into shares.
 *
 * A single copy of the offline key is both a single point of loss and a single
 * point of theft: lose the USB stick and the escrow is gone forever; have it
 * taken and the escrow is the attacker's. Shamir's scheme removes both at once.
 * With a threshold of two out of three, any two shares rebuild the key and any
 * one share reveals *nothing whatsoever* — not a weakened key, not a smaller
 * search space. Nothing.
 *
 * That last property is what makes this worth implementing rather than, say,
 * cutting the key in half: half a key is half a key, and halves the work of
 * guessing the rest. A share is statistically independent of the secret.
 *
 * ── How ──
 *
 * Each byte of the secret becomes the constant term of a random polynomial of
 * degree `threshold - 1` over GF(256). A share is that polynomial evaluated at
 * a distinct non-zero point. Any `threshold` points determine the polynomial —
 * and therefore the constant — by interpolation; any fewer leave every possible
 * constant equally likely, because for each candidate there is exactly one
 * polynomial through the known points.
 *
 * GF(256) is used rather than arithmetic modulo a prime because a byte maps
 * onto it exactly, so a share is the same length as the secret with no padding
 * and no bias.
 */

import { MediaCryptoError, base64ToBytes, bytesToBase64 } from "./crypto.js";

/*
 * Log and antilog tables for GF(256) with the AES polynomial, generator 3.
 * Multiplication becomes addition of logarithms, which keeps the hot loop free
 * of branches that would otherwise depend on secret bytes.
 */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    let next = (x << 1) ^ x; // multiply by the generator, 3
    if (next & 0x100) next ^= 0x11b; // reduce by the AES polynomial
    x = next & 0xff;
  }
  // Doubled so an index sum up to 508 needs no modulo in the inner loop.
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
}

function mul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}

function div(a: number, b: number): number {
  if (b === 0) throw new MediaCryptoError("Division by zero in GF(256).");
  if (a === 0) return 0;
  return EXP[LOG[a]! + 255 - LOG[b]!]!;
}

/** Version byte, so a share format change is detected rather than misread. */
const SHARE_VERSION = 1;
/** Bytes of digest carried alongside, to catch a wrong or damaged share. */
const CHECK_BYTES = 4;

export interface SplitOptions {
  /** How many shares to produce. */
  shares: number;
  /** How many are needed to rebuild. */
  threshold: number;
}

/**
 * Split a secret into printable shares.
 *
 * Each share is `version.index.payload` in base64. The index matters — it is
 * the point the polynomial was evaluated at — so shares carry it rather than
 * relying on the order they happen to be typed back in.
 */
export async function splitSecret(
  secret: Uint8Array,
  { shares, threshold }: SplitOptions,
): Promise<string[]> {
  if (threshold < 2) throw new MediaCryptoError("A threshold below 2 splits nothing.");
  if (shares < threshold) throw new MediaCryptoError("Fewer shares than the threshold is unusable.");
  if (shares > 255) throw new MediaCryptoError("GF(256) allows at most 255 shares.");
  if (secret.length === 0) throw new MediaCryptoError("Nothing to split.");

  /*
   * A digest of the secret travels with every share so that combining the wrong
   * ones fails loudly. Without it, a mistyped share yields plausible-looking
   * bytes that are simply the wrong key — and the failure would surface much
   * later, as an unopenable archive, with no hint that the shares were at fault.
   */
  const digest = new Uint8Array(
    await crypto.subtle.digest("SHA-256", secret.slice().buffer as ArrayBuffer),
  ).subarray(0, CHECK_BYTES);

  const body = new Uint8Array(secret.length + CHECK_BYTES);
  body.set(digest, 0);
  body.set(secret, CHECK_BYTES);

  const outputs = Array.from({ length: shares }, () => new Uint8Array(body.length));

  for (let position = 0; position < body.length; position++) {
    // Constant term is the secret byte; the rest is fresh randomness per byte.
    const coefficients = crypto.getRandomValues(new Uint8Array(threshold));
    coefficients[0] = body[position]!;

    for (let s = 0; s < shares; s++) {
      const x = s + 1; // never 0 — that point is the secret itself
      let value = 0;
      // Horner, highest coefficient first.
      for (let c = threshold - 1; c >= 0; c--) {
        value = mul(value, x) ^ coefficients[c]!;
      }
      outputs[s]![position] = value;
    }
  }

  return outputs.map((payload, index) => formatShare(index + 1, payload));
}

/** Rebuild the secret. Order does not matter; each share names its own point. */
export async function combineShares(shares: string[]): Promise<Uint8Array> {
  if (shares.length < 2) throw new MediaCryptoError("At least two shares are needed.");

  const parsed = shares.map(parseShare);
  const length = parsed[0]!.payload.length;
  if (parsed.some((share) => share.payload.length !== length)) {
    throw new MediaCryptoError("Those shares are not from the same secret.");
  }
  if (new Set(parsed.map((share) => share.index)).size !== parsed.length) {
    // Two copies of one share are one point, and would silently interpolate to
    // rubbish rather than failing.
    throw new MediaCryptoError("The same share was supplied more than once.");
  }

  const body = new Uint8Array(length);
  for (let position = 0; position < length; position++) {
    // Lagrange interpolation evaluated at x = 0.
    let accumulated = 0;
    for (let i = 0; i < parsed.length; i++) {
      let term = parsed[i]!.payload[position]!;
      for (let j = 0; j < parsed.length; j++) {
        if (i === j) continue;
        const xi = parsed[i]!.index;
        const xj = parsed[j]!.index;
        term = mul(term, div(xj, xi ^ xj));
      }
      accumulated ^= term;
    }
    body[position] = accumulated;
  }

  const secret = body.subarray(CHECK_BYTES);
  const expected = new Uint8Array(
    await crypto.subtle.digest("SHA-256", secret.slice().buffer as ArrayBuffer),
  ).subarray(0, CHECK_BYTES);

  for (let i = 0; i < CHECK_BYTES; i++) {
    if (body[i] !== expected[i]) {
      throw new MediaCryptoError(
        "These shares do not rebuild a valid secret. Check for a mistyped share, " +
          "or one belonging to a different key.",
      );
    }
  }

  return new Uint8Array(secret);
}

function formatShare(index: number, payload: Uint8Array): string {
  return `${String(SHARE_VERSION)}.${String(index)}.${bytesToBase64(payload)}`;
}

function parseShare(share: string): { index: number; payload: Uint8Array } {
  const parts = share.trim().split(".");
  if (parts.length !== 3) throw new MediaCryptoError(`Malformed share: ${share.slice(0, 12)}…`);

  const [version, index, payload] = parts as [string, string, string];
  if (Number(version) !== SHARE_VERSION) {
    throw new MediaCryptoError(`Unsupported share version ${version}.`);
  }

  const point = Number(index);
  if (!Number.isInteger(point) || point < 1 || point > 255) {
    throw new MediaCryptoError("A share index must be between 1 and 255.");
  }

  try {
    return { index: point, payload: base64ToBytes(payload) };
  } catch {
    throw new MediaCryptoError("A share payload is not valid base64.");
  }
}
